import { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';

function PreferencesSettings({ userData, onUpdateUserData, showNotification }) {
  const { theme: currentTheme, updateTheme } = useTheme();
  const { updateUserPreferences } = useAuth();
  
  // User preferences with default values if not provided
  const [preferences, setPreferences] = useState({
    theme: userData.preferences?.theme || currentTheme || 'dark',
    notifications: {
      email: userData.preferences?.notifications?.email ?? true,
      push: userData.preferences?.notifications?.push ?? true,
      updates: userData.preferences?.notifications?.updates ?? false
    },
    language: userData.preferences?.language || 'es',
    dateFormat: userData.preferences?.dateFormat || 'DD/MM/YYYY'
  });

  // Sync with ThemeContext when userData changes
  useEffect(() => {
    if (userData.preferences?.theme && userData.preferences.theme !== currentTheme) {
      updateTheme(userData.preferences.theme);
    }
  }, [userData.preferences?.theme, currentTheme, updateTheme]);

  // Handle theme selection
  const handleThemeChange = (theme) => {
    setPreferences(prev => ({
      ...prev,
      theme
    }));
  };

  // Handle notification toggle
  const handleNotificationToggle = (type) => {
    setPreferences(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [type]: !prev.notifications[type]
      }
    }));
  };

  // Handle language selection
  const handleLanguageChange = (e) => {
    setPreferences(prev => ({
      ...prev,
      language: e.target.value
    }));
  };

  // Handle date format selection
  const handleDateFormatChange = (format) => {
    setPreferences(prev => ({
      ...prev,
      dateFormat: format
    }));
  };

  // Save all preferences
  const handleSavePreferences = () => {
    // Update the theme in ThemeContext
    updateTheme(preferences.theme);
    
    // Update user preferences in AuthContext
    updateUserPreferences(preferences);
    
    // Update user data
    onUpdateUserData({ preferences });
    showNotification("Preferencias guardadas correctamente");
  };

  // Theme options
  const themes = [
    { id: 'dark', name: 'Oscuro', bg: '#121212', accent: '#ff1b6d' },
    { id: 'light', name: 'Claro', bg: '#f8f9fa', accent: '#ff1b6d' }
  ];

  // Language options
  const languages = [
    { code: 'es', name: 'Español' },
    { code: 'en', name: 'English' },
    { code: 'fr', name: 'Français' },
    { code: 'de', name: 'Deutsch' }
  ];

  // Date format options
  const dateFormats = [
    { id: 'DD/MM/YYYY', label: 'DD/MM/YYYY', example: '31/12/2025' },
    { id: 'MM/DD/YYYY', label: 'MM/DD/YYYY', example: '12/31/2025' },
    { id: 'YYYY-MM-DD', label: 'YYYY-MM-DD', example: '2025-12-31' }
  ];

  return (
    <div className="p-6 space-y-8">
      {/* Theme Section */}
      <div>
        <h3 className="text-lg font-medium text-white mb-4">Tema</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {themes.map(theme => (
            <div 
              key={theme.id}
              className={`bg-[#252525] rounded-lg border ${
                preferences.theme === theme.id 
                  ? 'border-[#ff1b6d]' 
                  : 'border-[#3a3a3a]'
              } p-4 cursor-pointer relative`}
              onClick={() => handleThemeChange(theme.id)}
            >
              {preferences.theme === theme.id && (
                <div className="absolute -top-2 -right-2">
                  <div className="h-6 w-6 rounded-full bg-[#ff1b6d] flex items-center justify-center">
                    <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                </div>
              )}
              <div 
                className="h-20 rounded-md mb-2 flex items-center justify-center"
                style={{ backgroundColor: theme.id === 'dark' ? '#121212' : '#f8f9fa' }}
              >
                <div 
                  className="h-8 w-8 rounded-full"
                  style={{ backgroundColor: theme.accent }}
                ></div>
              </div>
              <div className={`font-medium ${theme.id === 'dark' ? 'text-white' : 'text-gray-800 dark:text-white'}`}>
                Modo {theme.name}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Notifications Section */}
      <div>
        <h3 className="text-lg font-medium text-white mb-4">Notificaciones</h3>
        <div className="space-y-4 bg-[#252525] rounded-lg border border-[#3a3a3a] p-4">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-white font-medium">Notificaciones por correo</span>
              <p className="text-sm text-gray-400">Recibe actualizaciones importantes por email</p>
            </div>
            <label className="inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                checked={preferences.notifications.email} 
                onChange={() => handleNotificationToggle('email')} 
                className="sr-only peer"
              />
              <div className="relative w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#ff1b6d]"></div>
            </label>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <span className="text-white font-medium">Notificaciones push</span>
              <p className="text-sm text-gray-400">Recibe alertas y recordatorios inmediatos</p>
            </div>
            <label className="inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                checked={preferences.notifications.push} 
                onChange={() => handleNotificationToggle('push')} 
                className="sr-only peer"
              />
              <div className="relative w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#ff1b6d]"></div>
            </label>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <span className="text-white font-medium">Actualizaciones de producto</span>
              <p className="text-sm text-gray-400">Mantente informado sobre nuevas funciones</p>
            </div>
            <label className="inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                checked={preferences.notifications.updates} 
                onChange={() => handleNotificationToggle('updates')} 
                className="sr-only peer"
              />
              <div className="relative w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#ff1b6d]"></div>
            </label>
          </div>
        </div>
      </div>

      {/* Language Section */}
      <div>
        <h3 className="text-lg font-medium text-white mb-4">Idioma</h3>
        <div className="space-y-6">
          {/* Language Selection */}
          <div>
            <label htmlFor="language" className="block text-sm font-medium text-gray-400 mb-1">Idioma de la aplicación</label>
            <select
              id="language"
              value={preferences.language}
              onChange={handleLanguageChange}
              className="w-full px-3 py-2 bg-[#252525] border border-[#3a3a3a] rounded-md text-white focus:outline-none focus:ring-1 focus:ring-[#ff1b6d]"
            >
              {languages.map(lang => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
            </select>
            <p className="mt-2 text-sm text-gray-400">
              Esta configuración cambiará el idioma de toda la interfaz
            </p>
          </div>

          {/* Date Format Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-3">Formato de fecha</label>
            <div className="space-y-2">
              {dateFormats.map(format => (
                <div key={format.id} className="flex items-center">
                  <input
                    type="radio"
                    id={`format-${format.id}`}
                    name="dateFormat"
                    checked={preferences.dateFormat === format.id}
                    onChange={() => handleDateFormatChange(format.id)}
                    className="h-4 w-4 text-[#ff1b6d] border-gray-600 bg-[#252525] focus:ring-[#ff1b6d] focus:ring-offset-[#252525]"
                  />
                  <label htmlFor={`format-${format.id}`} className="ml-3 flex items-center">
                    <span className="text-white">{format.label}</span>
                    <span className="ml-2 text-sm text-gray-400">({format.example})</span>
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div>
        <button
          type="button"
          className="px-4 py-2 bg-[#ff1b6d] text-white rounded-md hover:bg-[#ff1b6d]/90 focus:outline-none focus:ring-2 focus:ring-[#ff1b6d]/50"
          onClick={handleSavePreferences}
        >
          Guardar Preferencias
        </button>
      </div>
    </div>
  );
}

export default PreferencesSettings;

